// Formatação e vocabulário compartilhados pela área administrativa.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export const fmtMoeda = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Centavos, como o banco guarda `planos.preco` e `faturas.valor`. */
export const fmtCentavos = (v: number) => fmtMoeda((v || 0) / 100);

export const fmtData = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

/** `2026-08-01` → `Agosto/2026`. Os meses chegam do banco como data. */
export const fmtMesAno = (iso: string) => {
  const data = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const mes = data.toLocaleDateString('pt-BR', { month: 'long' });
  return `${mes.charAt(0).toUpperCase()}${mes.slice(1)}/${data.getFullYear()}`;
};

export const fmtMesCurto = (iso: string) => {
  const data = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const mes = data.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return mes.charAt(0).toUpperCase() + mes.slice(1);
};

export const statusCliente: Record<string, { label: string; cor: string }> = {
  ativo: { label: 'Ativo', cor: 'bg-green-50 text-green-700 border-green-200' },
  inativo: { label: 'Inativo', cor: 'bg-secondary-100 text-secondary-900 border-secondary-200' },
  cancelado: { label: 'Cancelado', cor: 'bg-red-50 text-red-700 border-red-200' },
};

/**
 * Status da assinatura no vocabulário do domínio.
 *
 * `assinaturas.status` (migration 0002) já é a tradução do Stripe feita pelo
 * webhook. A tela de cliente lê daqui, e não de `profiles.subscription_status`,
 * que guarda o termo cru do Stripe.
 */
export const statusAssinatura: Record<string, { label: string; cor: string }> = {
  ativa: { label: 'Ativa', cor: 'bg-green-50 text-green-700 border-green-200' },
  trialing: { label: 'Em teste', cor: 'bg-blue-50 text-blue-700 border-blue-200' },
  inadimplente: { label: 'Inadimplente', cor: 'bg-amber-50 text-amber-700 border-amber-200' },
  nao_paga: { label: 'Não paga', cor: 'bg-red-50 text-red-700 border-red-200' },
  incompleta: {
    label: 'Incompleta',
    cor: 'bg-secondary-100 text-secondary-900 border-secondary-200',
  },
  cancelada: { label: 'Cancelada', cor: 'bg-red-50 text-red-700 border-red-200' },
};

/** Assinatura vigente → cliente ativo; sem assinatura → inativo. */
export function statusDoCliente(
  status: string | null | undefined,
): 'ativo' | 'inativo' | 'cancelado' {
  if (!status) return 'inativo';
  if (status === 'ativa' || status === 'trialing' || status === 'inadimplente') return 'ativo';
  if (status === 'cancelada') return 'cancelado';
  return 'inativo';
}

export function mapSubscriptionStatus(subStatus: string | null): {
  status: 'ativo' | 'inativo' | 'cancelado';
  label: string;
  cor: string;
} {
  if (!subStatus) return { status: 'inativo', ...statusCliente.inativo };
  const map: Record<string, 'ativo' | 'inativo' | 'cancelado'> = {
    active: 'ativo',
    trialing: 'ativo',
    past_due: 'ativo',
    unpaid: 'inativo',
    canceled: 'cancelado',
    incomplete: 'inativo',
    incomplete_expired: 'inativo',
    paused: 'inativo',
  };
  const s = map[subStatus] || 'inativo';
  return { status: s, ...statusCliente[s] };
}

// ─── Catálogo de planos da administração ────────────────────────────
//
// Não vem de `config?chave=planos`: aquele catálogo é a vitrine, filtrada por
// `visibilidade = 'ativo'`, e existe para a página de preços poder ser lida sem
// sessão. A administração precisa dos planos internos também — um cliente em
// `plano-parceria-sindiconet` apareceria como "Sem plano" na lista.
//
// Antes disto o mapa estava no código, com três dos nove planos e os preços
// escritos à mão. Quem assinou Básico, Intermediário ou Grande aparecia como
// "Sem plano — R$ 0,00" em toda a área administrativa (ADR-0011).

export interface PlanoDaPlataforma {
  id: string;
  nome: string;
  preco: number;
  visibilidade: 'ativo' | 'interno';
}

let cachePlanos: PlanoDaPlataforma[] | null = null;
let planosEmVoo: Promise<PlanoDaPlataforma[]> | null = null;

async function buscarPlanos(): Promise<PlanoDaPlataforma[]> {
  if (cachePlanos) return cachePlanos;
  if (planosEmVoo) return planosEmVoo;

  planosEmVoo = Promise.resolve(
    supabase.from('planos').select('id, nome, preco, visibilidade').order('preco'),
  )
    .then(({ data, error }) => {
      if (error) throw new Error(error.message);
      cachePlanos = (data ?? []) as PlanoDaPlataforma[];
      return cachePlanos;
    })
    .finally(() => {
      planosEmVoo = null;
    });

  return planosEmVoo;
}

/** Descarta o cache — use depois de alterar planos pela tela de administração. */
export function invalidarPlanos(): void {
  cachePlanos = null;
}

/**
 * Planos indexados por id, para a lista resolver o plano de cada cliente.
 *
 * O detalhe do cliente não precisa disto: `cliente_da_plataforma()` já devolve
 * o plano com nome e preço, resolvido pela precedência assinatura → perfil.
 */
export function usePlanos(): {
  planos: PlanoDaPlataforma[];
  porId: Record<string, PlanoDaPlataforma>;
  carregando: boolean;
} {
  const [planos, setPlanos] = useState<PlanoDaPlataforma[]>(() => cachePlanos ?? []);
  const [carregando, setCarregando] = useState(cachePlanos === null);

  const carregar = useCallback(() => {
    let cancelado = false;
    buscarPlanos()
      .then((lista) => {
        if (!cancelado) setPlanos(lista);
      })
      .catch(() => {
        // Um select sem opções é ruim; travar a tela por causa dele é pior.
        if (!cancelado) setPlanos([]);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(carregar, [carregar]);

  const porId: Record<string, PlanoDaPlataforma> = {};
  for (const p of planos) porId[p.id] = p;

  return { planos, porId, carregando };
}
